import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AuthActions } from "../../../store/ducks/auth";
import {
	OperationalContactOrigin,
	OperationalDashboardActions,
	OperationalIndicator,
	OperationalLastContact,
} from "../../../store/ducks/operationalDashboard";
import {
	buildWhatsAppUrl,
	formatBrazilianPhone,
	normalizeBrazilianWhatsAppPhone,
} from "../utils/whatsApp";
import {
	createIdempotencyKey,
	PatientContactApiError,
	registerWhatsAppContact,
	toOperationalLastContact,
} from "../services/patientContactService";

const PAGE_SIZE = 4;

export type ContactActionState = "idle" | "registering" | "opening" | "error";

interface ContactActionStatus {
	state: ContactActionState;
	error?: string;
	idempotencyKey?: string;
	forbidden?: boolean;
}

export interface ContactIndicatorPatient {
	id: string;
	name: string;
	initials: string;
	photoUrl?: string | null;
	date: string;
	days: number;
	phone: string;
	originalPhone: string;
	message: string;
	lastContact: OperationalLastContact | null;
	lastContactAt?: string;
	lastContactLabel?: string;
	lastContactAuthorLabel?: string;
}

interface ContactIndicatorConfig {
	indicator: OperationalIndicator;
	stateKey: "prontosReavaliacao" | "pacientesInativos";
	contactOrigin: OperationalContactOrigin;
	message: (firstName: string, clinicName: string) => string;
}

export interface ContactIndicatorInitialPatient {
	ID_USUARIO: number;
	USUARIO_NOME?: string;
	USUARIO_FOTO?: string | null;
	USUARIO_CONTATO?: string | null;
	ULTIMA_AVALIACAO?: { DATA_HORA?: string | null } | null;
	ULTIMO_CONTATO?: OperationalLastContact | null;
}

interface ContactIndicatorOptions {
	initialPatient?: ContactIndicatorInitialPatient | null;
	onContactStarted?: (patientId: string) => void;
}

const formatLastContactLabel = (occurredAt?: string) => {
	if (!occurredAt) return undefined;
	const contactDate = moment(occurredAt);
	if (!contactDate.isValid()) return undefined;
	const days = Math.max(
		moment().startOf("day").diff(contactDate.startOf("day"), "days"),
		0
	);
	if (days === 0) return "Contato iniciado hoje";
	if (days === 1) return "Contato iniciado há 1 dia";
	return `Contato iniciado há ${days} dias`;
};

const contactErrorMessage = (error: unknown): string => {
	if (!(error instanceof PatientContactApiError)) {
		return "Não foi possível registrar o contato. Tente novamente.";
	}
	switch (error.status) {
		case 400:
			return "Os dados do contato são inválidos. Revise e tente novamente.";
		case 401:
			return "Sua sessão expirou. Entre novamente para continuar.";
		case 403:
			return "Você não tem permissão para iniciar este contato.";
		case 409:
			return "Não foi possível reutilizar esta tentativa. Tente novamente.";
		case 422:
			return "O contato não pôde ser registrado. Tente novamente.";
		default:
			return error.status >= 500
				? "O serviço está indisponível. Tente novamente."
				: error.message;
	}
};

const shouldKeepIdempotencyKey = (error: unknown) =>
	!(error instanceof PatientContactApiError) ||
	error.status === 422 ||
	error.status >= 500;

export const useContactIndicatorModalViewModel = (
	isOpen: boolean,
	config: ContactIndicatorConfig,
	options: ContactIndicatorOptions = {}
) => {
	const dispatch = useDispatch();
	const indicatorData = useSelector(
		(state: any) => state?.operationalDashboard?.[config.stateKey]
	);
	const clinicName = useSelector(
		(state: any) =>
			state?.configs?.config_relatorio?.CONFIG_EMPRESA_NOME || "Kinology"
	);
	const loggedUser = useSelector((state: any) => state?.auth?.user);
	const updatingContactPatientIds = useSelector(
		(state: any) => state?.operationalDashboard?.updatingContactPatientIds || {}
	);
	const contactUpdateErrors = useSelector(
		(state: any) => state?.operationalDashboard?.contactUpdateErrors || {}
	);
	const [searchQuery, setSearchQuery] = useState("");
	const [activeContactId, setActiveContactId] = useState<string | null>(null);
	const [loadingMoreRequested, setLoadingMoreRequested] = useState(false);
	const [patients, setPatients] = useState<ContactIndicatorPatient[]>([]);
	const [phoneErrors, setPhoneErrors] = useState<Record<string, boolean>>({});
	const [contactActions, setContactActions] = useState<
		Record<string, ContactActionStatus>
	>({});
	const inFlightContactIds = useRef<Record<string, boolean>>({});

	useEffect(() => {
		if (!isOpen) return;
		setSearchQuery("");
		setActiveContactId(
			options.initialPatient ? String(options.initialPatient.ID_USUARIO) : null
		);
		setLoadingMoreRequested(false);
		setPhoneErrors({});
		setContactActions({});
		inFlightContactIds.current = {};
	}, [isOpen, options.initialPatient?.ID_USUARIO]);

	useEffect(() => {
		if (!isOpen) return;
		const timeout = setTimeout(
			() => {
				setLoadingMoreRequested(false);
				dispatch(
					OperationalDashboardActions.operational_indicator_request({
						indicator: config.indicator,
						page: 1,
						limit: PAGE_SIZE,
						search: searchQuery,
					})
				);
			},
			searchQuery.trim() ? 300 : 0
		);
		return () => clearTimeout(timeout);
	}, [config.indicator, dispatch, isOpen, searchQuery]);

	useEffect(() => {
		if (!indicatorData?.loading) setLoadingMoreRequested(false);
	}, [indicatorData?.loading]);

	useEffect(() => {
		if (!isOpen) return;
		const sourcePatients = [...(indicatorData?.items || [])];
		if (
			!searchQuery.trim() &&
			options.initialPatient &&
			!sourcePatients.some(
				(patient: any) =>
					Number(patient?.ID_USUARIO) ===
					Number(options.initialPatient?.ID_USUARIO)
			)
		) {
			sourcePatients.unshift(options.initialPatient);
		}
		const mappedPatients = sourcePatients.map((patient: any) => {
			const id = String(patient?.ID_USUARIO);
			const name = patient?.USUARIO_NOME || "";
			const names = name.trim().split(/\s+/).filter(Boolean);
			const firstName = names[0] || "";
			const initials =
				names.length > 1
					? `${names[0].charAt(0)}${names[names.length - 1].charAt(
							0
					  )}`.toUpperCase()
					: name.charAt(0).toUpperCase();
			const rawDate = patient?.ULTIMA_AVALIACAO?.DATA_HORA;
			const lastEvaluation = moment(rawDate);
			const lastContact: OperationalLastContact | null =
				patient?.ULTIMO_CONTATO ?? null;

			return {
				id,
				name,
				initials,
				photoUrl: patient?.USUARIO_FOTO || null,
				date: lastEvaluation.isValid()
					? lastEvaluation.format("DD/MM/YYYY")
					: "Sem data",
				days: lastEvaluation.isValid()
					? Math.max(moment().diff(lastEvaluation, "days"), 0)
					: 0,
				phone: patient?.USUARIO_CONTATO
					? formatBrazilianPhone(patient.USUARIO_CONTATO)
					: "",
				originalPhone: patient?.USUARIO_CONTATO || "",
				message: config.message(firstName, clinicName),
				lastContact,
				lastContactAt: lastContact?.DATA_HORA,
				lastContactLabel: formatLastContactLabel(lastContact?.DATA_HORA),
				lastContactAuthorLabel:
					Number(lastContact?.ID_AVALIADOR) === Number(loggedUser?.ID_USUARIO)
						? "por você"
						: lastContact?.AVALIADOR_NOME
						? `por ${lastContact.AVALIADOR_NOME}`
						: lastContact
						? "por outro profissional"
						: undefined,
			};
		});

		setPatients((current) =>
			mappedPatients.map((patient: ContactIndicatorPatient) => {
				const edited = current.find((item) => item.id === patient.id);
				return edited
					? { ...patient, phone: edited.phone, message: edited.message }
					: patient;
			})
		);
	}, [
		clinicName,
		config,
		indicatorData?.items,
		isOpen,
		loggedUser?.ID_USUARIO,
		options.initialPatient,
		searchQuery,
	]);

	const setContactAction = (patientId: string, status: ContactActionStatus) =>
		setContactActions((current) => ({
			...current,
			[patientId]: status,
		}));

	const handleSearchChange = (query: string) => {
		setSearchQuery(query);
		setActiveContactId(null);
	};

	const handleToggleContact = (patientId: string) => {
		if (contactActions[patientId]?.forbidden) return;
		setActiveContactId((current) => (current === patientId ? null : patientId));
		setPhoneErrors((current) => ({ ...current, [patientId]: false }));
	};

	const handlePhoneChange = (patientId: string, value: string) => {
		setPatients((current) =>
			current.map((patient) =>
				patient.id === patientId
					? { ...patient, phone: formatBrazilianPhone(value) }
					: patient
			)
		);
		setPhoneErrors((current) => ({ ...current, [patientId]: false }));
		setContactActions((current) =>
			current[patientId]?.forbidden
				? current
				: {
						...current,
						[patientId]: { state: "idle" },
				  }
		);
	};

	const handleMessageChange = (patientId: string, message: string) => {
		setPatients((current) =>
			current.map((patient) =>
				patient.id === patientId ? { ...patient, message } : patient
			)
		);
	};

	const registerAndOpenWhatsApp = async (
		patient: ContactIndicatorPatient,
		url: string,
		whatsappWindow: Window,
		idempotencyKey: string
	) => {
		try {
			setContactAction(patient.id, {
				state: "registering",
				idempotencyKey,
			});
			const contact = await registerWhatsAppContact(
				loggedUser.token,
				{
					ID_USUARIO: Number(patient.id),
					ORIGEM: config.contactOrigin,
				},
				idempotencyKey
			);
			const lastContact = toOperationalLastContact(contact);
			dispatch(
				OperationalDashboardActions.patient_whatsapp_contact_register_success(
					patient.id,
					lastContact
				)
			);
			setContactAction(patient.id, { state: "opening" });
			whatsappWindow.location.href = url;
			options.onContactStarted?.(patient.id);
			setContactAction(patient.id, { state: "idle" });
			setActiveContactId(null);
		} catch (error) {
			whatsappWindow.close();
			const status =
				error instanceof PatientContactApiError ? error.status : undefined;
			setContactAction(patient.id, {
				state: "error",
				error: contactErrorMessage(error),
				idempotencyKey: shouldKeepIdempotencyKey(error)
					? idempotencyKey
					: undefined,
				forbidden: status === 403,
			});
			if (status === 401) dispatch(AuthActions.auth_logout());
		} finally {
			inFlightContactIds.current[patient.id] = false;
		}
	};

	const handleSendWhatsApp = (patientId: string) => {
		const patient = patients.find((item) => item.id === patientId);
		if (
			!patient ||
			!loggedUser?.token ||
			inFlightContactIds.current[patientId] ||
			updatingContactPatientIds[patientId] ||
			contactActions[patientId]?.forbidden
		)
			return;

		const url = buildWhatsAppUrl(patient.phone, patient.message);
		if (!url) {
			setPhoneErrors((current) => ({ ...current, [patientId]: true }));
			return;
		}

		const idempotencyKey =
			contactActions[patientId]?.idempotencyKey || createIdempotencyKey();
		const whatsappWindow = window.open("about:blank", "_blank");
		if (!whatsappWindow) {
			setContactAction(patientId, {
				state: "error",
				error: "Permita a abertura de pop-ups para continuar pelo WhatsApp.",
			});
			return;
		}

		whatsappWindow.opener = null;
		inFlightContactIds.current[patientId] = true;
		setPhoneErrors((current) => ({ ...current, [patientId]: false }));
		setContactAction(patientId, {
			state: "registering",
			idempotencyKey,
		});

		const normalizedPhone = normalizeBrazilianWhatsAppPhone(patient.phone);
		const normalizedOriginalPhone = normalizeBrazilianWhatsAppPhone(
			patient.originalPhone
		);
		const continueToRegistration = () =>
			void registerAndOpenWhatsApp(
				patient,
				url,
				whatsappWindow,
				idempotencyKey
			);

		if (normalizedPhone === normalizedOriginalPhone) {
			continueToRegistration();
			return;
		}

		dispatch(
			OperationalDashboardActions.patient_contact_update_request({
				patientId,
				phone: normalizedPhone.slice(2),
				onSuccess: continueToRegistration,
				onFailure: () => {
					whatsappWindow.close();
					inFlightContactIds.current[patientId] = false;
					setContactAction(patientId, {
						state: "error",
						error: "Não foi possível atualizar o telefone. Tente novamente.",
						idempotencyKey,
					});
				},
			})
		);
	};

	const handleLoadMore = () => {
		if (indicatorData?.loading || !indicatorData?.paginacao?.temProxima) return;
		setLoadingMoreRequested(true);
		dispatch(
			OperationalDashboardActions.operational_indicator_request({
				indicator: config.indicator,
				page: (indicatorData.paginacao.pagina || 1) + 1,
				limit: PAGE_SIZE,
				search: searchQuery,
				append: true,
			})
		);
	};

	const retry = () => {
		setLoadingMoreRequested(false);
		dispatch(
			OperationalDashboardActions.operational_indicator_request({
				indicator: config.indicator,
				page: 1,
				limit: PAGE_SIZE,
				search: searchQuery,
			})
		);
	};

	const canOpenWhatsApp = (patientId: string) => {
		const patient = patients.find((item) => item.id === patientId);
		return !!normalizeBrazilianWhatsAppPhone(patient?.phone || "");
	};
	const contactActionState = (patientId: string): ContactActionState =>
		contactActions[patientId]?.state || "idle";
	const isUpdatingContact = (patientId: string) =>
		!!updatingContactPatientIds[patientId] ||
		contactActionState(patientId) === "registering" ||
		contactActionState(patientId) === "opening";
	const contactUpdateError = (patientId: string) =>
		contactActions[patientId]?.error || contactUpdateErrors[patientId] || null;
	const isContactForbidden = (patientId: string) =>
		!!contactActions[patientId]?.forbidden;

	return {
		searchQuery,
		activeContactId,
		isLoading: !!indicatorData?.loading,
		isLoadingMore: !!indicatorData?.loading && loadingMoreRequested,
		loadMoreCount: PAGE_SIZE,
		phoneErrors,
		filteredPatients: patients,
		hasExtraPatients: !!indicatorData?.paginacao?.temProxima,
		totalPatients: indicatorData?.total || 0,
		error: indicatorData?.error || null,
		canOpenWhatsApp,
		contactActionState,
		isUpdatingContact,
		contactUpdateError,
		isContactForbidden,
		handleSearchChange,
		handleToggleContact,
		handlePhoneChange,
		handleMessageChange,
		handleSendWhatsApp,
		handleLoadMore,
		retry,
	};
};
